import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { QrCodeIcon, RefreshCwIcon } from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useState } from "react";
import {
  useWhatsAppWorkerQrQuery,
  useWhatsAppWorkerStatusQuery,
} from "../api/queries";

export function WhatsAppWorkerQrPanel() {
  const workerQuery = useWhatsAppWorkerStatusQuery({ refetchInterval: 3_000 });
  const isConnected =
    workerQuery.data?.connected === true ||
    workerQuery.data?.status === "connected";
  const needsQr =
    !isConnected &&
    (workerQuery.data?.needsQr === true ||
      workerQuery.data?.status === "needs_qr" ||
      workerQuery.data?.status === "initializing");
  const qrQuery = useWhatsAppWorkerQrQuery(needsQr);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);

  const qrPayload = qrQuery.data?.qr ?? null;

  useEffect(() => {
    if (!qrPayload) {
      setPreviewSrc(null);
      return;
    }

    let cancelled = false;
    void QRCode.toDataURL(qrPayload, {
      width: 280,
      margin: 2,
      errorCorrectionLevel: "L",
    }).then((url) => {
      if (!cancelled) setPreviewSrc(url);
    });

    return () => {
      cancelled = true;
    };
  }, [qrPayload]);

  if (!needsQr || isConnected) return null;

  return (
    <Card className="gap-0 h-full border-amber-500/30 bg-amber-500/[0.02]">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 pb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <QrCodeIcon className="size-5 shrink-0 text-amber-600" />
            <CardTitle className="text-base">ເຊື່ອມ WhatsApp Studio</CardTitle>
          </div>
          <CardDescription className="mt-1">
            ສະແກນ QR ດ້ວຍແອັບ WhatsApp → ເຊື່ອມອຸປະກອນ
          </CardDescription>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => {
            void qrQuery.refetch();
            void workerQuery.refetch();
          }}
          disabled={qrQuery.isFetching}
        >
          <RefreshCwIcon className="mr-1 size-4" />
          ໂຫຼດໃໝ່
        </Button>
      </CardHeader>
      <CardContent className="grid gap-6 pt-0 lg:grid-cols-2 lg:items-center">
        <div className="flex justify-center">
          <div className="rounded-xl border-2 border-dashed border-amber-500/40 bg-white p-4 shadow-sm">
            {previewSrc ? (
              <img src={previewSrc} alt="QR WhatsApp" className="size-48" />
            ) : (
              <div className="flex size-48 items-center justify-center bg-muted/50 px-4 text-center text-muted-foreground text-sm">
                {qrQuery.isFetching
                  ? "ກຳລັງໂຫຼດ QR..."
                  : "QR ໝົດອາຍຸ — ກົດໂຫຼດໃໝ່"}
              </div>
            )}
          </div>
        </div>
        <ol className="list-decimal space-y-2 pl-4 text-muted-foreground text-sm">
          <li>ເປີດ WhatsApp ບນໂທລະສັບ Studio</li>
          <li>ໄປ ການຕັ້ງຄ່າ → ເຊື່ອມອຸປະກອນ → ເຊື່ອມອຸປະກອນ</li>
          <li>ສະແກນ QR ດ້ານຊ້າຍ</li>
          <li className="text-amber-700 text-xs dark:text-amber-400">
            QR ໝົດອາຍົໄວ (~1 ນາທີ) — ຖ້າສະແກນບໍ່ທັນ ກົດໂຫຼດໃໝ່
          </li>
        </ol>
      </CardContent>
    </Card>
  );
}
